import { createAsyncThunk } from "@reduxjs/toolkit";
import request from "../../../../service";
import { IErrorResponse, IRootState } from "../../../../types";
import {
    setActiveSubjectsCount,
    setCourseFormat,
    setEducationalStatistics,
    setIsLearningPlanInformation,
    setIsLearningType,
    setLearningPlanDeleted,
    setLearningPlanSchedule,
    setLearningPlanTerm,
    setPlanSettings,
} from "./learningPlanSlice";
import { IEditPlanSchedule, IGetPlanSchedule, ILearningPlanCreate } from "../types";
import { ILearningTermSchedule } from "./types";
import { setProfessionalsData } from "@pages/EducationalStructure/Profession/store/professionsSlice";
import { setFaculty } from "@pages/EducationalStructure/Faculty/store/facultySlice";
import { setEducationalBlockData } from "@pages/LearningManagement/EducationalBlocks/store/educationalBlocksSlice";
import { setAcademicYear } from "@pages/EducationalSettings/AcademicYear/store/academicYearSlice";
import { setDepartment } from "@pages/EducationalStructure/AcademicDepartment/store/academicDepartmentSlice";
import { setCompetences } from "@pages/LearningManagement/Competences/store/competencesSlice";
import { setEducationalDegree } from "@pages/EducationalStructure/EducationalDegree/store/educationDegreeSlice";
import { setLearningFormat } from "@pages/EducationalStructure/LearningFormat/store/learningFormatSlice";
import { setStudyYears } from "@pages/EducationalSettings/StudyYears/store/studyYearsSlice";
import { setEducationalSymbols } from "../PlanSettingsPages/EducationalSymbols/store/educationalSymbolsSlice";
import { setTeachingTypes } from "@pages/EducationalSettings/TeachingType/store/teachingTypeSlice";
import { setSectors } from "@pages/EducationalSettings/EducationalSectors/store/educationalSectorsSlice";
import { setSubjectCheck } from "@pages/EducationalSettings/SubjectCheckType/store/subjectCheckTypeSlice";
import { setLearningSettings } from "../PlanSettingsPages/LearningSettings/store/learningSettingsSlice";
import { IEditLearningPlanState } from "@pages/LearningManagement/LearningPlan/components/LearningPlanOpen/components/LearningSymbolsTable/types";

const GetLearningPlanCombined = createAsyncThunk(
    "get/learning/plan/combined",
    async (_, { dispatch, rejectWithValue, getState }) => {
        try {
            const response = await request.get("learning-plan-combined-data");
            const lang = getState() as IRootState;
            const defaultLanguage = lang.languages.defaultLanguage;
            dispatch(setEducationalBlockData({ data: response.data["educational_blocs"] }));
            dispatch(setAcademicYear({ data: response.data["academic_years"], defaultLanguage }));
            dispatch(setDepartment({ data: response.data["academic_departments"], defaultLanguage }));
            dispatch(setCompetences(response.data.competences));
            dispatch(setEducationalDegree({ data: response.data["educational_degrees"], defaultLanguage }));
            dispatch(setLearningFormat({ data: response.data["learning_formats"], defaultLanguage }));
            dispatch(setStudyYears(response.data["study_years"]));
            dispatch(setEducationalSymbols({ data: response.data["educational_symbols"], defaultLanguage }));
            dispatch(setTeachingTypes({ data: response.data["teaching_types"], defaultLanguage }));
            dispatch(setSectors({ data: response.data["education_sectors"], defaultLanguage }));
            dispatch(setIsLearningType({ data: response.data["learning_type"], defaultLanguage }));
            dispatch(setSubjectCheck({ data: response.data["subject_check_types"], defaultLanguage }));
            dispatch(setIsLearningPlanInformation({ data: response.data["learning_plan"], defaultLanguage }));
            dispatch(setLearningSettings(response.data["learning_component_setting"]));
            dispatch(setCourseFormat({ data: response.data["course_format"], defaultLanguage }));
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const CreateLearningPlanInformation = createAsyncThunk(
    "post/learning/plan/information",
    async (data: ILearningPlanCreate, { rejectWithValue }) => {
        try {
            const response = await request.post("learning-plan", data);

            return response.data.learning_plan;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const GetPlanSchedule = createAsyncThunk(
    "get/plan/schedule",
    async (data: IGetPlanSchedule, { rejectWithValue, dispatch }) => {
        try {
            const response = await request.post("learning-plan-schedule", data);
            dispatch(setLearningPlanSchedule(response.data));
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const LearningTermSchedule = createAsyncThunk(
    "create/term/schedule",
    async (data: ILearningTermSchedule, { rejectWithValue }) => {
        try {
            const response = await request.post("learning-plan-subjects/term-schedule", data);
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const SetLearningTermSchedule = createAsyncThunk(
    "set/term/schedule",
    async (learning_plan_id: number, { rejectWithValue, dispatch }) => {
        try {
            const response = await request.post("learning-plan-subjects/get_term_by_parent_id", {
                learning_plan_id,
            });
            dispatch(setLearningPlanTerm(response.data));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const EditPlanSchedule = createAsyncThunk(
    "edit/plan/schedule",
    async (data: IEditPlanSchedule[], { rejectWithValue }) => {
        try {
            const response = await request.post("learning-plan-schedule-update", data);
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const GetProfessionsByFaculty = createAsyncThunk(
    "get/professions/faculty",
    async (id: number, { rejectWithValue, dispatch, getState }) => {
        try {
            const response = await request.post(`/professions/by_faculty/${id}`);
            const lang = getState() as IRootState;
            const defaultLanguage = lang.languages.defaultLanguage;
            dispatch(
                setProfessionalsData({
                    data: response.data,
                    pagesSize: 0,
                    currentPage: 0,
                    totalPages: 0,
                    defaultLanguage,
                })
            );
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const GetFacultyByLearning = createAsyncThunk(
    "get/faculty/learning",
    async (id: number, { rejectWithValue, dispatch, getState }) => {
        try {
            const response = await request.post(`learning-formats/get-faculty-by-learning-format/${id}`);
            const lang = getState() as IRootState;
            const defaultLanguage = lang.languages.defaultLanguage;
            dispatch(setFaculty({ data: response.data, defaultLanguage }));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const GetLearningType = createAsyncThunk("get/learning/types", async (_, { rejectWithValue, dispatch, getState }) => {
    try {
        const response = await request.get("learning-type");
        const lang = getState() as IRootState;
        const defaultLanguage = lang.languages.defaultLanguage;
        dispatch(setIsLearningType({ data: response.data, defaultLanguage }));
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const GetCourseFormat = createAsyncThunk("get/course/Format", async (_, { rejectWithValue, dispatch, getState }) => {
    try {
        const response = await request.get("course-format");
        const lang = getState() as IRootState;
        const defaultLanguage = lang.languages.defaultLanguage;
        dispatch(setCourseFormat({ data: response.data, defaultLanguage }));
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const GetPlanStatistic = createAsyncThunk("get/plan/statistic", async (id: number, { rejectWithValue, dispatch }) => {
    try {
        const response = await request.post("learning-plan-schedule/get-statistic", {
            learning_plan_id: id,
        });
        dispatch(setPlanSettings(response.data.data));
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const DeleteLearningPlan = createAsyncThunk("delete/learning/plan", async (id: number, { rejectWithValue }) => {
    try {
        const response = await request.delete(`learning-plan/${id}`);
        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});
const DeletedLearningPlan = createAsyncThunk(
    "get/deleted/learning/plan",
    async (_, { rejectWithValue, dispatch, getState }) => {
        try {
            const response = await request.get("learning-plan/deleted-items");
            const lang = getState() as IRootState;
            const defaultLanguage = lang.languages.defaultLanguage;
            dispatch(setLearningPlanDeleted({ data: response.data, defaultLanguage }));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const RestoreLearningPlan = createAsyncThunk("restore/learning/plan", async (id: number, { rejectWithValue }) => {
    try {
        const response = await request.post(`/learning-plan/restore/${id}`);
        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const EditLearningPlan = createAsyncThunk(
    "put/learning/plan",
    async (data: IEditLearningPlanState, { rejectWithValue }) => {
        try {
            const response = await request.put(`/learning-plan/${data.learning_plan_id}`, data);
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);
const GetPlanSubject = createAsyncThunk(
    "post/get/learning-plan-subject",
    async ({ learning_plan_id }: { learning_plan_id: number }, { rejectWithValue, getState }) => {
        try {
            const lang = getState() as IRootState;
            const defaultLanguage = lang.languages.defaultLanguage;

            const response = await request.post("learning-plan-subjects/get-by-plan-id", {
                learning_plan_id,
                default_language: defaultLanguage,
            });

            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const GetSubjectDepartment = createAsyncThunk("get/subject/department", async (data: any, { rejectWithValue }) => {
    try {
        const response = await request.get(`/subjects/department/${data.id}/sector/${data.sectorID}`);

        return response.data.data;
    } catch (error) {
        const typedError = error as IErrorResponse;

        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

export const GetTermSymbolCountByParentId = createAsyncThunk(
    "get/term/symbol-count",
    async (learning_plan_id: number, { rejectWithValue }) => {
        try {
            const response = await request.post("/learning-plan-subjects/get_term_course_sum_week_by_parent_id", {
                learning_plan_id,
            });

            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response?.data?.message || "unexpected_fail");
        }
    }
);

const GetEducationalStatistic = createAsyncThunk(
    "learning/educational/statistic",
    async (learning_plan_id: number, { rejectWithValue, dispatch }) => {
        try {
            const response = await request.post("learning-plan-subjects/educational-statistic", {
                learning_plan_id,
            });
            dispatch(setEducationalStatistics(response.data));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const GetSearchLearningPlan = createAsyncThunk(
    "get/learning/search/plan",
    async (searchQuery: string | undefined, { dispatch, rejectWithValue, getState }) => {
        try {
            const response = await request.get("/learning-plan", {
                params: searchQuery ? { search: searchQuery, page_size: 100 } : {},
            });
            const lang = getState() as IRootState;
            const defaultLanguage = lang.languages.defaultLanguage;
            dispatch(setIsLearningPlanInformation({ data: response.data.data, defaultLanguage }));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const GetLearningPlanOptions = createAsyncThunk(
    "learning/plan/options",
    async (learning_plan_id: number, { rejectWithValue, dispatch }) => {
        try {
            const response = await request.get(`learning-plan-options/${learning_plan_id}`);

            const data = response.data.reduce((aggr: any, val: any) => {
                aggr[val.option_id] = Number(val.value);
                return aggr;
            }, {});

            dispatch(setActiveSubjectsCount(data));

            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);
export {
    GetProfessionsByFaculty,
    GetLearningType,
    GetCourseFormat,
    CreateLearningPlanInformation,
    GetPlanSchedule,
    EditPlanSchedule,
    GetPlanStatistic,
    GetFacultyByLearning,
    LearningTermSchedule,
    SetLearningTermSchedule,
    DeleteLearningPlan,
    DeletedLearningPlan,
    RestoreLearningPlan,
    EditLearningPlan,
    GetLearningPlanCombined,
    GetPlanSubject,
    GetSubjectDepartment,
    GetEducationalStatistic,
    GetSearchLearningPlan,
    GetLearningPlanOptions,
};
